import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface SensorGroupSourceTagProps {
  source: string | null;
  className?: string;
}

interface SensorGroupTypeTagProps {
  type: string | null;
  className?: string;
}

/**
 * Displays a colorful tag indicating the source system of a sensor group.
 * Uses color-blind friendly colors with sufficient contrast and clear text labels.
 *
 * Source values:
 * - 'Signal': Blue tag for Signal system groups
 * - 'Waterscope': Green tag for Waterscope groups
 * - 'Waterscope DMA Master': Purple tag for DMA master groups
 */
export function SensorGroupSourceTag({ source, className }: SensorGroupSourceTagProps) {
  if (!source) return null;

  // Color-blind friendly palette with sufficient contrast (WCAG AA compliant)
  const sourceStyles = {
    Signal: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900 dark:text-blue-200',
    Waterscope:
      'bg-green-100 text-green-800 border-green-200 dark:bg-green-900 dark:text-green-200',
    'Waterscope DMA Master':
      'bg-green-100 text-green-800 border-green-200 dark:bg-green-900 dark:text-green-200',
  };

  const style = sourceStyles[source as keyof typeof sourceStyles];
  if (!style) return null;

  return (
    <Badge className={cn('border', style, className)} variant='outline'>
      {source}
    </Badge>
  );
}

/**
 * Displays a grey tag showing the sensor group type.
 *
 * Type values include: 'Commercial', 'Consumer Group', 'Cooling Tower', 'DMA',
 * 'DMA Dynamic Group', 'Dynamic Group', 'Profile', 'Settings Group', 'Utility'
 */
export function SensorGroupTypeTag({ type, className }: SensorGroupTypeTagProps) {
  if (!type) return null;

  return (
    <Badge
      className={cn(
        'border bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300',
        className,
      )}
      variant='outline'
    >
      {type}
    </Badge>
  );
}
